import {
    Box,
    Divider,
    IconButton,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { DistributionWidgetContainer } from './Widgets/DistributionWidgetContainer';
import { RankingWidgetContainer } from './Widgets/RankingWidgetContainer';
import { FilterSection } from './FilterSection';
import { NumericInput } from './NumericInputField';

const SetItem = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `${theme.spacing(1)} solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(2),
    gap: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
}));

export interface SetCardProps {
    index: number;
    displayIndex: number;
    set: Models.SetsPlayground.SetDefinition;
    onChangeName: (index: number, name: string) => void;
    onChangePriority: (index: number, priority: number) => void;
    onChangeDistribution: (index: number, dist: Models.SetsPlayground.SetDistribution) => void;
    onChangeRanking: (index: number, ranking: Models.SetsPlayground.SetRanking) => void;
    onChangeFilter: (index: number, filter: Models.SetsPlayground.SetFilterRequest) => void;
    onChangeDesignPersonalizationContextId: (index: number, id: string) => void;
    onDelete: (index: number) => void;
    productKey?: string;
    onChangeProductKey?: (key: string) => void;
}

export const SetCard = ({
    index,
    displayIndex,
    set,
    onChangeName,
    onChangePriority,
    onChangeDistribution,
    onChangeRanking,
    onChangeFilter,
    onChangeDesignPersonalizationContextId,
    onDelete,
    productKey,
    onChangeProductKey,
}: SetCardProps): JSX.Element => {
    const commitPriority = (val: number | undefined): void => {
        if (typeof val === 'number') {
            onChangePriority(index, val);
        }
    };

    return (
        <SetItem>
            <Box alignItems="center" display="flex" gap={2}>
                <NumericInput
                    required
                    label="Priority"
                    min={1}
                    value={set.priority}
                    onBlur={commitPriority}
                    onKeyDown={(e, val): void => {
                        if (e.key === 'Enter') {
                            commitPriority(val);
                        }
                    }}
                />
                <Box flexGrow={1} />
                <IconButton aria-label="delete set" color="error" onClick={(): void => onDelete(index)}>
                    <DeleteOutline />
                </IconButton>
            </Box>
            <Box display="flex" gap={2}>
                <TextField
                    fullWidth
                    required
                    label="Name"
                    placeholder={`Set ${displayIndex + 1}`}
                    size="small"
                    value={set.name}
                    onChange={(e): void => onChangeName(index, e.target.value)}
                />
                <TextField
                    fullWidth
                    label="Design Personalization Context ID"
                    placeholder="Optional context ID"
                    size="small"
                    value={set.designPersonalizationContextId || ''}
                    onChange={(e): void => onChangeDesignPersonalizationContextId(index, e.target.value)}
                />
            </Box>
            <Typography fontWeight="medium" sx={{ mb: -4 }} variant="body1">
                Distribution
            </Typography>
            <Divider />
            <DistributionWidgetContainer index={index} set={set} onChangeDistribution={onChangeDistribution} />
            <Typography fontWeight="medium" sx={{ mb: -4 }} variant="body1">
                Ranking
            </Typography>
            <Divider />
            <RankingWidgetContainer index={index} set={set} onChangeRanking={onChangeRanking} />
            <FilterSection
                showTitle
                filter={set.filters}
                productKey={productKey}
                onChangeFilter={(filter): void => onChangeFilter(index, filter)}
                onChangeProductKey={onChangeProductKey}
            />
        </SetItem>
    );
};
